import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ActionIcon,Button, Group, Tooltip  } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { IconFilter, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { formatDateToString } from "@utils/dateValidation";
import {
  filterByCategory,
  filterByDay,
  filterByMonth,
  filterByYear,
} from "@utils/filterDialogFilters";
import { getLabelForRecordType } from "@utils/timelineLabel";

import { PAGE_TITLE } from "@constants/index";

import { timelineQKeys } from "@hooks/queries/timeline/queryKeys";
import { useGetTimelineForPatient } from "@hooks/queries/timeline/useGetTimelineForPatient";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { TimelineItem } from "@customTypes/timeline";

import { TimelineFilterDialog } from "./components/TimelineFilterDialog";
import { StyledTimeline } from "./Timeline.styled";


const filterTimelineData = (
  timelineData:
    | {
        newData: TimelineItem[];
        years: any;
      }
    | null
    | undefined,
  filters: {
    year: string;
    month: string;
    day: string;
    category: string;
  },
  setFilteredTimelineData: (arg: [] | TimelineItem[]) => void
) => {
  let filteredData = timelineData?.newData || [];
  // console.log("Filters:", filters);

  if (filters.year && filteredData) {
    filteredData = filterByYear(filters.year, filteredData);
  }

  if (filters.month) {
    filteredData = filterByMonth(filters.month, filteredData);
  }

  if (filters.day) {
    filteredData = filterByDay(filters.day, filteredData);
  }

  if (filters.category) {
    switch (filters.category) {
      case "consultation":
        filteredData = filterByCategory("consultation", filteredData);
        break;

      case "laboratory":
        filteredData = filterByCategory("laboratory", filteredData);
        break;

      case "radiology":
        filteredData = filterByCategory("radiology", filteredData);
        break;

      case "prescription":
        filteredData = filterByCategory("prescription", filteredData);
        break;

      case "immunization":
        filteredData = filterByCategory("immunization", filteredData);
        break;

      case "footnote":
        filteredData = filterByCategory("footnote", filteredData);
        break;

      case "treatment":
        filteredData = filterByCategory("treatment", filteredData);
        break;

      default:
        break;
    }
  }

  setFilteredTimelineData(filteredData || []);

  if (!filters.year && !filters.month && !filters.day && !filters.category) {
    setFilteredTimelineData(timelineData?.newData || []);
  }
};

export const Timeline = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("timeline")} • ${PAGE_TITLE}`);
  const { patientId } = useParams();
  const [filters, setFilters] = useQueryParams({
    year: withDefault(StringParam, ""),
    month: withDefault(StringParam, ""),
    day: withDefault(StringParam, ""),
    category: withDefault(StringParam, ""),
  });
  const queryClient = useQueryClient();

  const [filteredTimelineData, setFilteredTimelineData] = useState<
    TimelineItem[] | []
  >([]);
  // const [filters, setFilters] = useState({
  //   year: "",
  //   month: "",
  //   day: "",
  //   category: "",
  // });
  const {
    data: timelineData,
    isLoading: isTimelineDataLoading,
    isRefetching: isTimelineRefetching,
    fetchStatus: timelineFetchStatus,
  } = useGetTimelineForPatient({
    patientId: patientId as string,
  });
  const { buttonRef } = useRefreshAnimation({
    isRefreshing: isTimelineRefetching,
  });

  useEffect(() => {
    filterTimelineData(timelineData, filters, setFilteredTimelineData);
  }, [filters.year, filters.month, filters.day, filters.category]);

  useEffect(() => {
    if (timelineData) {
      setFilteredTimelineData(timelineData.newData);
    }
    if (filters?.year || filters?.month || filters?.day || filters?.category) {
      filterTimelineData(timelineData, filters, setFilteredTimelineData);
    }
  }, [timelineData]);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledTimeline>
          <Dialog.Root>
            <TimelineFilterDialog
              filters={filters}
              setFilters={setFilters}
              years={timelineData?.years || []}
            />
            <Group className="header">
              <h2>{t("timeline")}</h2>
              <Tooltip
                label={t("refresh-data")}
                position="right-end"
                fz={12}
                events={{ focus: true, hover: true, touch: true }}>
                <ActionIcon
                  ref={buttonRef}
                  variant={"default"}
                  size={"md"}
                  onClick={() => {
                    queryClient.invalidateQueries(
                      timelineQKeys.patient(patientId as string)
                    );
                  }}>
                  <IconRefresh
                    className={"refresh-icon"}
                    color="var(--mantine-color-black)"
                    size={16}
                  />
                </ActionIcon>
              </Tooltip>
            </Group>

            <div className="timeline-header">
              {filters.year ||
              filters.month ||
              filters.day ||
              filters.category ? (
                <>
                  <p>{t("applied-filters")}</p>
                  <ul className="filter-tags">
                    {[
                      {
                        filterTitle: "year",
                        filterValue: filters.year,
                      },
                      {
                        filterTitle: "month",
                        filterValue: filters.month,
                      },
                      {
                        filterTitle: "day",
                        filterValue: filters.day,
                      },
                      {
                        filterTitle: "category",
                        filterValue: filters.category,
                      },
                    ]
                      .filter(item => item.filterValue?.length)
                      .map(({ filterTitle, filterValue }, index) => (
                        <li className="filter-tags-item" key={index}>
                          {filterTitle === "year" ? (
                            <span>{filterValue}</span>
                          ) : (
                            <span>{t(`${filterValue}`)}</span>
                          )}
                          <button
                            onClick={() => {
                              if (filterTitle === "year") {
                                setFilters({ year: "" });
                                // setUrlFilters(prev => ({ ...prev, year: "" }));
                              } else if (filterTitle === "month") {
                                setFilters({ ...filters, month: "" });
                                // setUrlFilters(prev => ({ ...prev, month: "" }));
                              } else if (filterTitle === "day") {
                                setFilters({ ...filters, day: "" });
                                // setUrlFilters(prev => ({ ...prev, day: "" }));
                              } else if (filterTitle === "category") {
                                setFilters({ ...filters, category: "" });
                                // setUrlFilters(prev => ({
                                //   ...prev,
                                //   category: "",
                                // }));
                              }
                            }}>
                            <Cross2Icon />
                          </button>
                        </li>
                      ))}
                  </ul>
                </>
              ) : (
                <p>{t("no-filters")}</p>
              )}
              <Group justify="end" w={"100%"}>
                <Dialog.Trigger asChild>
                  <Button
                    variant="outline"
                    leftSection={<IconFilter size={16} />}>
                    {t("filters")}
                  </Button>
                </Dialog.Trigger>
              </Group>
            </div>

            <ul className="timeline">
              {!filteredTimelineData?.length && isTimelineDataLoading ? (
                <p className="empty-label">{t("loading")}</p>
              ) : null}

              {filteredTimelineData?.length && !isTimelineDataLoading
                ? filteredTimelineData.map(({ date, data }, index) => (
                    <li className="timeline-item" key={index}>
                      <span className="date">{formatDateToString(date)}</span>
                      <div className="decor decor-1" aria-hidden></div>
                      <div className="decor decor-2" aria-hidden></div>

                      <ul className="timeline-events">
                        {data.map(({ recordType, title, doctor }, index) => (
                          <li className="timeline-events-item" key={index}>
                            <div
                              className={`decor ${recordType}`}
                              aria-hidden></div>
                            <span className={`label ${recordType}`}>
                              {t(`${recordType}`)}
                            </span>
                            <div className="text-wrapper">
                              <p className="title">{title}</p>
                              <p className="doctor">
                                {getLabelForRecordType(recordType)}{" "}
                                {doctor?.fullName}
                              </p>
                            </div>
                            {/*{footnote ? (
                                <div className="footnote">{footnote}</div>
                              ) : null} */}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))
                : null}

              {!isTimelineDataLoading && !filteredTimelineData?.length ? (
                <p className="empty-label">{t("no-data")}</p>
              ) : null}
            </ul>
          </Dialog.Root>
        </StyledTimeline>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
